import React, { useState, useEffect, useRef } from "react";
import {
  Collapse,
  Form,
  Input,
  Button,
  Row,
  Col,
  Tabs,
  Select,
  Tag,
} from "antd";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import {
  MinusOutlined,
  PlusOutlined,
  FileTextOutlined, // submitted
  FileSyncOutlined, // in revision
  FileSearchOutlined, // In Progress
  FileDoneOutlined, //approved
  ExceptionOutlined, // rejected
  FileUnknownOutlined, //cancelled
  CheckOutlined, // done
  EditOutlined
} from "@ant-design/icons";

import moment from "moment";

import {
  Messages,
  Notify,
  openNotification,
  util
} from '@seims/shared';

import EventsGeneralInfo from "./EventsGeneralInfo";
import EventObjectives from "./EventObjectives";
import EventVenue from "./EventVenue";
import EventPartners from "./EventPartners";
import EventOrganiser from "./EventOrganiser";
import EventFacillitator from "./EventFacillitator";
import Feedbacknotes from "./Feedbacknotes";

import { recordError } from "../../../../utils/logService";

import service from "../../../../services/eventsService";
import { isAuthorizedRole } from "@seims/shared/src/security";

const { Panel } = Collapse;

const ViewEventDetails = () => {
  const [currentStatus, setCurrentStatus] = useState("");
  const [statusColour, setStatusColour] = useState("");
  const [statusIcon, setStatusIcon] = useState();
  const [generalInfo, setGeneralInfo] = useState({});
  const [objectives, setObjectives] = useState({});
  const [venue, setVenue] = useState({});
  const [partners, setPartners] = useState({});
  const [organiser, setOrganiser] = useState({});
  const [facillitators, setFacillitators] = useState({});
  const [eventType, setEventType] = useState("V");
  const [feedbackNotes, setFeedbackNotes] = useState({});
  const [organiserRevised, setOrganiserRevised] = useState(false);
  const [assignVisible, setAssignVisible] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formRefs = [useRef(null)];
  
  const [formData, setFormData] = useState({
    status: undefined,
    notes: "",
  });

  useEffect(() => {
    prePopulate();
  }, []);

  useEffect(() => {
    setEventType(type);
    statusColourSwitch(currentStatus);
  }, [currentStatus, eventType, assignVisible]);

  const { key, type } = useParams();
  const location = useLocation();
  let navigate = useNavigate();

  const formName = "ViewEventDetailsForm";
  const [form] = Form.useForm();

  const formItemLayout = {
    labelCol: {
      span: 8,
    },
    wrapperCol: {
      span: 16,
    },
  };
  const prePopulate = async () => {
    try {
      if (key) {
        const id = key.substring(key.indexOf("x") + 1, key.length);
        const response = await service.getEventDetails(id);

        if (!location.state) {
          openNotification(
            "Events",
            "System is redirecting to event list",
            Notify.INFO
          );
          navigate("/events-management", { replace: true });

          return;
        }
        if (response && response.status === 200) {
          const { data } = response;

          const generalInfo = {
            name: data.name,
            description: data.description,
            theme: data.groupOrTheme,
            targetParticipants: data.targetParticipants,
            type: data.type,
            eventDate: data.dates,
            totalParticipants: data.totalParticipants
          };
          const venue = {
            name: data.venue,
            venueType: data.venueType,
            eventAddress: data.eventAddress,
          };
          const organiser = {
            organiserFirstname: data.organiserFirstname,
            organiserLastname: data.organiserLastname,
            organiserEmail: data.organiserEmail,
            years: data.experience?.years,
            details: data.experience?.details,
          };
          setGeneralInfo(generalInfo);
          setObjectives(data.objectives);
          setVenue(venue);
          setPartners(data.partners);
          setOrganiser(organiser);
          setOrganiserRevised(data.organiserRevised);
          setFacillitators(data.facilitators);
          setCurrentStatus(data.status);
          setFeedbackNotes(data.feedbackNotes);
        }
      }
    } catch (error) {
      recordError(error);
    }
  };
  const statusColourSwitch = (status) => {
    switch (status?.trim()) {
      case "Submitted":
        setStatusColour("event_status-submitted");
        setStatusIcon(<FileTextOutlined />);
        setEventType("V");
        setAssignVisible(false);
        break;
      case "In Progress":
        setStatusColour("event_status-review");
        setStatusIcon(<FileSearchOutlined />);
        setEventType("R");
        setAssignVisible(true);
        break;
      case "In Revision":
        setStatusColour("event_status-revision");
        setStatusIcon(<FileSyncOutlined />);
        setEventType("A");
        break;
      case "Approved":
        setStatusColour("event_status-approved");
        setStatusIcon(<FileDoneOutlined />);
        setEventType("A");
        setAssignVisible(true);
        break;
      case "Rejected":
        setStatusColour("event_status-rejected");
        setStatusIcon(<ExceptionOutlined />);
        setEventType("A");
        setAssignVisible(true);
        break;
      case "Cancelled":
        setStatusColour("event_status-cancelled");
        setStatusIcon(<FileUnknownOutlined />);
        setEventType("A");
        setAssignVisible(true);
        break;
      case "Done":
        setStatusColour("event_status-done");
        setStatusIcon(<CheckOutlined />);
        setEventType("A");
        setAssignVisible(true);
        break;
      default:
        setStatusColour("");
    }
  };
  const handleStatusChange = (status) => {
    setCurrentStatus(status);
  };

  const changeStatus = async () => {

    setIsSubmitting(true);
    let msg;
    let action = "Event status";
    const id = key.substring(key.indexOf("x") + 1, key.length);

    const data = {
      eventId: id,
      action: "Review",
      notes: "Assigned ",
    };

    try {
      const response = await service.updateEventStatus(data);
      if (response.status === 200 || response.status === 201) {
        msg = util.parseMessage(Messages.UPDATE_STATUS_ASSIGN, "Event");
        openNotification(action, msg, Notify.SUCCESS);
        setAssignVisible(true);
        setEventType("R");
        setCurrentStatus("In Progress");
      } else {
        msg = util.parseMessage(Messages.UPDATE_FAILURE_STATUS, "Event status");
        openNotification(action, msg, Notify.ERROR);
      }
    } catch (error) {
      msg = util.parseMessage(Messages.UPDATE_FAILURE_STATUS, "Event status");
      openNotification(action, msg, Notify.ERROR);

      recordError(error);
    }

    setIsSubmitting(false);
    return true;
  };

  const onSave = async () => {

    setIsSubmitting(true);
    let msg;
    let action = "Event status";
     const eventId = key.substring(key.indexOf("x") + 1, key.length);

    const data = {
      eventId: eventId,
      action: formRefs[0].current?.getFieldValue("status"),
      notes: formRefs[0].current?.getFieldValue("notes")};
    try {
      // const hide = message.loading("Submitting in progress, please wait...", 0);

      var statusUpdate = data.action === "Approve" ? "Approved"
        : data.action === "Reject" ? "Rejected"
          : data.action === "Send for revision" ? "In Revision"
            : "Unknown Status Action"; // Fallback for safety
      const response = await service.updateEventStatus(data);

      if (response.status === 200 || response.status === 201) {
            msg = util.parseMessages(Messages.UPDATE_STATUS, "Event", statusUpdate);
            openNotification(action, msg, Notify.SUCCESS);
            returnHome();
      }
      else {
        msg = util.parseMessages(Messages.UPDATE_FAILURE_STATUS, "Event", statusUpdate);
        openNotification(action, msg, Notify.ERROR);

      }
        } catch (error) {
    
          msg = util.parseMessages("Feedback must be provided for Rejected operation", "Event", statusUpdate);
          openNotification(action, msg, Notify.WARNING);
    
          recordError(error)
        }

    setIsSubmitting(false);
    return true;
  }

  const onBackClick = () => {
    returnHome();
  };

    const returnHome = () => {
      const state = location?.state;
      if (state) {
        const { from } = state;
        navigate(from);
  
        const { currentPage, pageSize } =
          location.state?.data;
        navigate(from, {
          state: {
            currentPage: currentPage,
            pageSize: pageSize,
          },
        });
      } else {
        openNotification(
          "Events",
          "System is redirecting to event list",
          Notify.WARNING
        );
        navigate("/events-management", { replace: true });
  
        return;
      }
    };
  
  const Tab1 = () => {
    return (
      <>
        <Collapse
          className="event-panel"
          defaultActiveKey={["1"]}
          expandIcon={({ isActive }) =>
            isActive ? <MinusOutlined /> : <PlusOutlined />
          }
          expandIconPosition="end"
        >
          <Panel header="General Event Info" key="1">
            <EventsGeneralInfo data={generalInfo} status = {currentStatus} />
          </Panel>
          <Panel header="Objectives" key="2">
            <EventObjectives data={objectives} />
          </Panel>
          <Panel header="Venue" key="4">
            <EventVenue data={venue} />
          </Panel>
          <Panel
            header={`Event Partners (${
              partners?.length === undefined ? 0 : partners?.length
            }) `}
            key="Event Partners-5"
          >
            <EventPartners data={partners} />
          </Panel>
          <Panel header="Event Organiser" key="6">
            <EventOrganiser data={organiser} />
          </Panel>
          <Panel
            header={`Event Facilitators (${
              facillitators?.length === undefined ? 0 : facillitators?.length
            }) `}
            key="Event Organiser-7"
          >
            <EventFacillitator data={facillitators} />
          </Panel>
        </Collapse>
        <br />
        <Form {...formItemLayout} name={formName} form={form} layout="vertical">
          {(currentStatus === "In Progress" && isAuthorizedRole(["EVENTS_RATIFIER"])) && (
            <Row>
              <Col span={24}>
                <Feedbacknotes
                  status={currentStatus}
                  onStatusUpdate={handleStatusChange}
                  formRef={formRefs[0]}
                  formData={formData}
                />
              </Col>
            </Row>
          )}

          {currentStatus === "In Revision" && organiserRevised === true && (
            <Row>
              <Col span={24}>
                <Feedbacknotes
                  status={currentStatus}
                  onStatusUpdate={handleStatusChange}
                  formRef={formRefs[0]}
                  formData={formData}

                />
              </Col>
            </Row>
          )}
        </Form>
        <Row>
          <Col xs={24} className="right-align">
            {(eventType === "V" && assignVisible === false && isAuthorizedRole(["EVENTS_RATIFIER"])) && (
              <Button
                key="events-assign-to-self"
                type="primary"
                onClick={changeStatus}
                style={{ marginRight: 10 }}
                disabled={disabled || isSubmitting}
                loading={isSubmitting}
              >
                Assign to self
              </Button>
            )}
            {
              (currentStatus === "In Progress" && isAuthorizedRole(["EVENTS_RATIFIER"])) &&
              <Button
                key="events-save-ratification"
                type="primary"
                onClick={onSave}
                icon={<EditOutlined />}
                style={{ marginRight: 10 }}
                disabled={disabled || isSubmitting}
                loading={isSubmitting}
              >
                Save
              </Button>
            }
            &nbsp;
            <Button
              type="default"
              onClick={onBackClick}
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Back
            </Button>
          </Col>
        </Row>
        
      </>
    );
  };
  const Tab2 = () => {
    return (
      <>
        <Form
          {...formItemLayout}
          name={formName}
          form={form}
          layout="vertical"
          disabled
        >
          <div>
            {feedbackNotes?.map((feedback, index) => (
              <Row
                style={{ marginBottom: "10px", marginLeft: "20%" }}
                key={index}
              >
                <Col span={8}>
                  <span>
                    <div
                      dangerouslySetInnerHTML={{ __html: feedback.notes }}
                    ></div>
                  </span>
                </Col>
                <Col span={1}></Col>
                <Col span={7}>
                  {feedback.name},{" "}
                  <Tag color="green">
                    {moment(feedback.date).format("DD MMM YYYY, HH:mm")}
                  </Tag>
                </Col>
              </Row>
            ))}
          </div>
        </Form>
        <Row>
          <Col xs={24} className="right-align">
            <Button type="default" onClick={onBackClick}>Back</Button>
          </Col>
        </Row>
      </>
    );
  };
  const tabList = [
    {
      key: "1",
      label: `Event information`,
      children: <Tab1 />,
    },
    {
      key: "2",
      label: `Feedback Notes`,
      children: <Tab2 />,
    },
  ];
  const onClickledTab = async (tab) => {
    if (tab === 2) {
      await prePopulate();
    }
  };

  return (
    <>
      <div className="content">
        <Row>
          <Col xs={24} sm={8} md={8}>
            <h2>Event name : {generalInfo.name}</h2>
          </Col>
          <Col xs={24} sm={8} md={8} className="center-align">
            <div className={statusColour + " status-label"}>
              <div>
                <span className="status-icon">{statusIcon}</span>&nbsp;{" "}
                {currentStatus}
              </div>
            </div>
          </Col>
        </Row>
        <br />
        <Tabs
          className="event-information"
          defaultActiveKey="1"
          items={tabList}
          key="3"
          onChange={onClickledTab}
        />
      </div>
    </>
  );
};

export default ViewEventDetails;
